import type { Point } from '@ikegps/core-api';
import type { SVGRenderOptions } from '../../../draw';
import { type ApprovableInsightDrawable, CollectionModelDrawable, type DrawableWithLabel } from './drawable';
import { CollectionModelDrawableState } from './drawable.state';
import { CollectionModelDrawableLabel } from './label.drawable';
import { CollectionModelDrawableLinePoint } from './line.point.drawable';

/**
 * Line is a "Width Marker" which also has a Label, a start point, and an end point.
 */
export class CollectionModelDrawableLine extends CollectionModelDrawable implements DrawableWithLabel {
	static createWithLinePointsAndLabel(
		drawableState: CollectionModelDrawableState,
		id: string,
		startPoint: Point,
		endPoint: Point,
		labelPoint: Point,
		text?: string[],
		links?: string[]
	) {
		const label = new CollectionModelDrawableLabel(
			drawableState,
			`${id}:label`,
			labelPoint.x,
			labelPoint.y,
			id,
			text,
			links
		);
		const start = new CollectionModelDrawableLinePoint(drawableState, `${id}:start`, startPoint.x, startPoint.y, id);
		const end = new CollectionModelDrawableLinePoint(drawableState, `${id}:end`, endPoint.x, endPoint.y, id);
		const line = new CollectionModelDrawableLine(drawableState, id, label.id, start.id, end.id);

		return { line, start, end, label };
	}

	constructor(
		collectionModelDrawableState: CollectionModelDrawableState,
		drawableId: string,
		labelId: string,
		startId: string,
		endId: string
	) {
		super(collectionModelDrawableState, drawableId);
		this._labelId = labelId;
		this._startId = startId;
		this._endId = endId;
	}

	static isLine(t: CollectionModelDrawable): t is CollectionModelDrawableLine {
		return t instanceof CollectionModelDrawableLine;
	}

	override get x() {
		return this.start.x;
	}

	override get y() {
		return this.start.y;
	}

	protected _labelId: string;
	protected _startId: string;
	protected _endId: string;

	get start(): CollectionModelDrawableLinePoint {
		return this.drawableState.getDrawable<CollectionModelDrawableLinePoint>(this._startId);
	}

	get end(): CollectionModelDrawableLinePoint {
		return this.drawableState.getDrawable<CollectionModelDrawableLinePoint>(this._endId);
	}

	get label(): CollectionModelDrawableLabel {
		return this.drawableState.getDrawable<CollectionModelDrawableLabel>(this._labelId);
	}

	getLabelConnectionPoint(_options: SVGRenderOptions): Point {
		const startPoint = this.start;
		const endPoint = this.end;
		const midX = (startPoint.x + endPoint.x) / 2;
		const midY = (startPoint.y + endPoint.y) / 2;
		return { x: midX, y: midY };
	}

	get value(): number {
		return this.drawableState.collection.state
			.getGeometry(this.captureId)
			.calculateDistance(this.start.getVector(), this.end.getVector());
	}

	override remove() {
		super.remove();
		// Remove this line's label too, unless it has already been removed
		if (this.drawableState.hasDrawable(this._labelId)) {
			this.label.remove();
		}
	}

	override get isInsightMeasurement(): boolean {
		return false;
	}

	override isApprovableInsightDrawable(): this is ApprovableInsightDrawable {
		return false;
	}

	override get isVisible(): boolean {
		return true;
	}

	override clone(newParentDrawableState: CollectionModelDrawableState): CollectionModelDrawableLine {
		if (!newParentDrawableState.hasDrawable(this._labelId)) {
			this.label.clone(newParentDrawableState);
		}
		return new CollectionModelDrawableLine(newParentDrawableState, this.id, this._labelId, this._startId, this._endId);
	}

	override equals(other: CollectionModelDrawable): boolean {
		return (
			other instanceof CollectionModelDrawableLine &&
			this._startId === other._startId &&
			this._endId === other._endId &&
			this._labelId === other._labelId &&
			this._equals(other)
		);
	}
}
